/**
 * @file コンポーネント フル動画ページコンテンツ ロジック管理
 */

// ライブラリ読み込み
import React, { useEffect, useRef, useState } from "react";

// 言語データ読み込み
import { en, ja } from "~/src/data/languages";

// 関数読み込み
import {
    fnConvertSecondToTimeString,
    fnConvertTimeStringToSecond,
    fnGetCultureLanguage,
    fnGetParameter,
    fnGetSurgeonFullName,
    fnHandleApiErrors,
    fnHandleApiSuccess,
    fnPushDataLayerGtm,
} from "~/src/functions/common";
import { fnIsAndroid, fnIsIpad, fnIsIphone } from "~/src/functions/boolean";

// Presenter読み込み
import { Presenter } from "./presenter";

// コンポーネントを返す
export const Contents = (props) => {
    // ローカライズデータ
    const localized = window.listLocalized;

    /**
     * 変数
     */
    const isSponsorUser = window.dataUser.isSponsorUser;
    const isIdentificationUser = window.dataUser.isIdentificationUser; // 特定リンクユーザーか否か
    let languageCodeOperation; // 症例データの表示言語
    switch (fnGetParameter("cl")) {
        case "en":
            languageCodeOperation = "en";
            break;
        case "ja":
            languageCodeOperation = "ja";
            break;
        default:
            languageCodeOperation = window.listLanguageData.contentLanguage;
    }
    const languageCodeUI = fnGetCultureLanguage(); // UI表示言語
    const operationId = window.btoa(window.dataPage.operationId); // base64エンコードされた症例ID

    /**
     * 術前情報取得
     */
    const [hasPreoperation, setHasPreoperation] = useState(false); // 術前情報有無
    const [slidersPreoperation, setSlidersPreoperation] = useState([]); // 術前情報画像スライダー
    const [textPreoperation, setTextPreoperation] = useState(null); // 術前情報テキスト
    useEffect(() => {
        const api =
            "/api/operations/contents/before/" +
            operationId +
            "?language=" +
            languageCodeOperation;
        fetch(api)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    setHasPreoperation(true);
                    if (result.contentList) {
                        const tempSliders = Object.values(
                            result.contentList
                        ).map((data) => {
                            return data;
                        });
                        setSlidersPreoperation(tempSliders);
                    }
                    setTextPreoperation(result.operationInfoText);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    /**
     * 術後情報取得
     */
    const [hasPostoperation, setHasPostoperation] = useState(false); // 術後情報情報有無
    const [slidersPostoperation, setSlidersPostoperation] = useState([]); // 術後情報画像スライダー
    const [textPostoperation, setTextPostoperation] = useState(null); // 術後情報テキスト
    useEffect(() => {
        const api =
            "/api/operations/contents/after/" +
            operationId +
            "?language=" +
            languageCodeOperation;
        fetch(api)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    setHasPostoperation(true);
                    if (result.contentList) {
                        const tempSliders = Object.values(
                            result.contentList
                        ).map((data) => {
                            return data;
                        });
                        setSlidersPostoperation(tempSliders);
                    }
                    setTextPostoperation(result.operationInfoText);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    /**
     * 機器リスト取得
     */
    const [listSurgicalEquipment, setListSurgicalEquipment] = useState([]);
    useEffect(() => {
        const api =
            "/api/SurgicalEquipment/" +
            operationId +
            "?language=" +
            languageCodeOperation;
        fetch(api)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    const tempAdOperationListDevice = result.map(
                        (equipment) => {
                            let name = equipment.eqptName;
                            name += equipment.manufacturer
                                ? " " + equipment.manufacturer
                                : "";
                            name += equipment.standardSize
                                ? " " + equipment.standardSize
                                : "";
                            return {
                                link: equipment.linkUrl,
                                name,
                            };
                        }
                    );
                    setListSurgicalEquipment(tempAdOperationListDevice);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    /**
     * 手術情報取得
     */
    // Contents全体で使用
    const [isPaid, setIsPaid] = useState(false); // 有料コンテンツフラグ
    const [isPurchased, setIsPurchased] = useState(false); // 有料コンテンツ購入済み状態
    const [timeProgress, setTimeProgress] = useState("00:00:00"); // フル動画プレイヤー再生時間
    const [productId, setProductId] = useState(0); // 商品ID
    // ContentsControllerで使用
    const [isLoadingContentsController, setIsLoadingContentsController] = // コントローラーローディング状態
        useState(true);
    const [mainSurgeonId, setMainSurgeonId] = useState(null); // メイン執刀医ID
    // ContentsDataで使用（FullvideoPlayer専用以外）
    const [date, setDate] = useState(null); // 手術情報更新日
    const [hasDigest, setHasDigest] = useState(false); // ダイジェスト動画有無
    const [hasPointcontents, setHasPointcontents] = useState(false); // ポイントコンテンツ有無
    const [isLoadingContentsData, setIsLoadingContentsData] = useState(true); // データコンテンツローディング状態
    const [linkRelatedCasesCategory, setLinkRelatedCasesCategory] = // 同じカテゴリーの手術一覧リンク
        useState(null);
    const [linkRelatedCasesSurgeon, setLinkRelatedCasesSurgeon] = // 同じ執刀医の手術一覧リンク
        useState(null);
    const [listLanguage, setListLanguage] = useState([]); // 症例データの言語プルダウンデータ
    const [rateCount, setRateCount] = useState(0); // 評価数
    const [selectedContentsData, setSelectedContentsData] = // 表示中のデータコンテンツ
        useState("fullvideo");
    const [summary, setSummary] = useState(null); // 症例概要
    const [surgeonlist, setSurgeonlist] = useState([]); // 執刀医リスト
    const [taglist, setTaglist] = useState([]); // 症例タグリスト
    const [title, setTitle] = useState(null); // 症例タイトル
    const [view, setView] = useState(0); // 閲覧数
    // FullvideoPlayerで使用
    const [fairplayCer, setFairplayCer] = useState(null); // フル動画fairplay.cerファイルパス
    const [hasAudio, setHasAudio] = useState(true); // フル動画音声有無
    const [isLoadingFullvideo, setIsLoadingFullvideo] = useState(true); // フル動画プレイヤーローディング状態
    const [movieUrl, setMovieUrl] = useState(null); // フル動画URL
    const [timeTotalSecond, setTimeTotalSecond] = useState(0); // フル動画総時間の秒換算
    const [fairplayLaUrl, setFairplayLaUrl] = useState(null); // fairplayライセンスURL
    const [playreadyLaUrl, setPlayreadyLaUrl] = useState(null); // playreadyライセンスURL
    const [widevineLaUrl, setWidevineLaUrl] = useState(null); // widevineライセンスURL
    // ユーザープレイログで使用
    const [deviceLayout, setDeviceLayout] = useState([]); // レイアウト順機器ID格納リスト
    // ローディングエラーモーダルの表示管理
    const [isOpenModalErrorLoading, setIsOpenModalErrorLoading] =
        useState(false);
    const isLoadingFullvideoRef = useRef(isLoadingFullvideo);
    useEffect(() => {
        isLoadingFullvideoRef.current = isLoadingFullvideo;
    }, [isLoadingFullvideo]);
    // フル動画データの生成
    const fnGetDataFullvideo = (contents) => {
        for (const content of contents) {
            if (
                content !== undefined && // contentLayoutの該当番目にデータがある
                content[1] !== null && // contentLayoutの該当番目のデータにコンテンツデータがある
                content[1][0] !== undefined && // contentLayoutの該当番目のコンテンツデータに1つ以上のコンテンツがある
                content[1][0].type === "Video"
            ) {
                // contentLayoutの該当番目の1つ目のコンテンツが動画
                // 条件を満たす場合、動画データなのでデータをセットする
                return {
                    fairplayCer: content[1][0].fairplayCer, // fairplay.cerファイルパス
                    movieUrl: content[1][0].movieUrl, // 動画URL
                    fairplayLaUrl: content[1][0].fairplayLaUrl, // fairplayライセンスURL
                    playreadyLaUrl: content[1][0].playreadyLaUrl, // playreadyライセンスURL
                    widevineLaUrl: content[1][0].widevineLaUrl, // widevineライセンスURL
                };
            }
        }
        // 動画データがない場合、nullを返す
        return null;
    };
    useEffect(() => {
        // APIのURL生成
        const url = "/api/videos/" + operationId;
        const params = [];
        params.push("language=" + languageCodeOperation);
        if (fnGetParameter("playLogId")) {
            params.push("playLogId" + "=" + fnGetParameter("playLogId"));
        }
        if (fnGetParameter("from")) {
            params.push("progressTime" + "=" + fnGetParameter("from"));
        }
        const api = params.length > 0 ? url + "?" + params.join("&") : url;
        // ローディングが30秒たっても消えない場合は、ローディングエラーモーダルを表示
        setTimeout(() => {
            if (isLoadingFullvideoRef.current) {
                setIsOpenModalErrorLoading(true);
            }
        }, 30000);
        // 手術情報リクエスト
        fetch(api)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    // 商品IDをセット
                    setProductId(result.productId);
                    // 有料コンテンツフラグをセット
                    setIsPaid(result.paidContentFlag);
                    // 有料コンテンツ購入済み状態をセット
                    setIsPurchased(result.purchasedFlag);
                    // フル動画再生時間セット
                    setTimeProgress(result.progressTime);
                    // 手術情報更新日をセット
                    setDate(
                        result.operationUploadDate
                            .slice(0, 10)
                            .split("-")
                            .join("/")
                    );
                    // フル動画音声有無セット
                    setHasAudio(result.hasAudio);
                    // ダイジェスト動画有無をセット
                    setHasDigest(result.digest.hasDigest);
                    // ポイントコンテンツ有無をセット
                    setHasPointcontents(
                        result.pointContentInfo.hasOperationContents
                    );
                    // 同じカテゴリーの手術一覧リンクをセット（術式分類リンクになるよう暫定対応）
                    setLinkRelatedCasesCategory(
                        result.surgicaOperationName.link
                            .split("/")
                            .splice(0, 3)
                            .join("/")
                    );
                    // 同じ執刀医の手術一覧リンクをセット
                    setLinkRelatedCasesSurgeon(result.surgeons[0].link);
                    // 症例データの言語プルダウンデータをセット
                    //// 執刀医権限、管理者権限のフル動画ページの場合、日英ともに表示で固定
                    const languages =
                        props.isEditPage === true
                            ? ["en", "ja"]
                            : result.languages;
                    const tempListLanguage = languages.map((language) => {
                        let tempText;
                        let tempTitle;
                        switch (language) {
                            case "en":
                                tempText = en.name;
                                tempTitle = en.viewThisCase;
                                break;
                            case "ja":
                                tempText = ja.name;
                                tempTitle = ja.viewThisCase;
                                break;
                            default:
                                tempText = en.name;
                                tempTitle = en.viewThisCase;
                        }
                        return {
                            text: tempText,
                            title: tempTitle,
                            value: language,
                        };
                    });
                    setListLanguage(tempListLanguage);
                    // 評価数をセット
                    setRateCount(result.ratingCount);
                    // 症例概要をセット
                    setSummary(result.operationSummary);
                    // 執刀医リストをセット
                    const tempSurgeonlist = result.surgeons.map((surgeon) => {
                        // メイン執刀医の場合、メイン執刀医IDにセット
                        if (surgeon.main === true) {
                            setMainSurgeonId(surgeon.id);
                        }
                        return {
                            id: surgeon.id,
                            name: fnGetSurgeonFullName(
                                surgeon,
                                languageCodeOperation
                            ),
                        };
                    });
                    setSurgeonlist(tempSurgeonlist);
                    // 症例タグリストをセット
                    const tempTaglist = result.tagList.map((tag) => {
                        return {
                            id: tag.id,
                            name: tag.name,
                        };
                    });
                    setTaglist(tempTaglist);
                    // 症例タイトルをセット
                    setTitle(result.operationTitle);
                    // 閲覧数をセット
                    setView(result.viewCount);
                    if (
                        // 執刀医権限、管理者権限でない有料コンテンツかつ未購入の場合、フル動画データを読み込ませない
                        props.isEditPage !== true &&
                        result.paidContentFlag === true &&
                        result.purchasedFlag === false
                    ) {
                        // フル動画の読み込み制御フラグを進める
                        setIsLoadingFullvideo(false);
                        setIsCompletionMediaPlayerFullvideo(true);
                    }
                    else if (
                        // SEO対応：未ログインの場合、フル動画データを読み込ませない
                        !window.dataUser.isLogin
                    ) {
                        // フル動画の読み込み制御フラグを進める
                        setIsLoadingFullvideo(false);
                        setIsCompletionMediaPlayerFullvideo(true);
                    } else {
                        // 有料コンテンツでない、または購入済みの場合、フル動画データをセット
                        const contents = Object.entries(result.contentLayout);
                        const dataFullvideo = fnGetDataFullvideo(contents);
                        if (dataFullvideo !== null) {
                            // フル動画データをセットできた場合
                            setFairplayCer(dataFullvideo.fairplayCer); // fairplay.cerファイルパス
                            setMovieUrl(dataFullvideo.movieUrl); // 動画URL
                            setFairplayLaUrl(dataFullvideo.fairplayLaUrl); // fairplayライセンスURL
                            setPlayreadyLaUrl(dataFullvideo.playreadyLaUrl); // playreadyライセンスURL
                            setWidevineLaUrl(dataFullvideo.widevineLaUrl); // widevineライセンスURL
                        } else {
                            // フル動画データをセットできなかった場合
                            // playLogIdパラメーターがある場合、無しの状態で再度APIリクエスト
                            if (fnGetParameter("playLogId")) {
                                const newParams = params.filter(
                                    (p) =>
                                        p !==
                                        "playLogId" +
                                            "=" +
                                            fnGetParameter("playLogId")
                                );
                                const newApi =
                                    newParams.length > 0
                                        ? url + "?" + newParams.join("&")
                                        : url;
                                fetch(newApi)
                                    .then(fnHandleApiErrors)
                                    .then(fnHandleApiSuccess)
                                    .then((newResult) => {
                                        if (newResult !== null) {
                                            const newContents = Object.entries(
                                                newResult.contentLayout
                                            );
                                            const newDataFullvideo =
                                                fnGetDataFullvideo(newContents);
                                            // ユーザープレイログ無しで動画データをセットできた場合
                                            if (newDataFullvideo !== null) {
                                                setFairplayCer(
                                                    newDataFullvideo.fairplayCer
                                                ); // fairplay.cerファイルパス
                                                setMovieUrl(
                                                    newDataFullvideo.movieUrl
                                                ); // 動画URL
                                                setFairplayLaUrl(
                                                    newDataFullvideo.fairplayLaUrl
                                                ); // fairplayライセンスURL
                                                setPlayreadyLaUrl(
                                                    newDataFullvideo.playreadyLaUrl
                                                ); // playreadyライセンスURL
                                                setWidevineLaUrl(
                                                    newDataFullvideo.widevineLaUrl
                                                ); // widevineライセンスURL
                                            } else {
                                                // ユーザープレイログ無しで動画データをセットできなかった場合、フル動画プレイヤーローディングを解除（エラーメッセージを出すため）
                                                setIsLoadingFullvideo(false);
                                            }
                                        } else {
                                            setIsLoadingFullvideo(false);
                                        }
                                    })
                                    .catch((newError) => {
                                        console.error(newError);
                                        // フル動画プレイヤーローディングを解除（エラーメッセージを出すため）
                                        setIsLoadingFullvideo(false);
                                    });
                            } else {
                                // playLogIdパラメーターがない場合、フル動画プレイヤーローディングを解除（エラーメッセージを出すため）
                                setIsLoadingFullvideo(false);
                            }
                        }
                    }
                    // フル動画総時間の秒換算セット
                    setTimeTotalSecond(
                        fnConvertTimeStringToSecond(result.operationTime)
                    );
                    // レイアウト順機器ID格納リストをセット
                    if (Object.keys(result.contentLayout).length > 0) {
                        setDeviceLayout([
                            Object.keys(result.contentLayout)
                                .sort()[0]
                                .split("_")[1],
                        ]);
                    }
                    // コントローラーローディング状態をセット
                    setIsLoadingContentsController(false);
                    // データコンテンツローディング状態をセット
                    setIsLoadingContentsData(false);
                    // 有料コンテンツでない、または購入済みでtabパラメーターがnoteの場合、ノートを開く
                    if (
                        !(
                            result.paidContentFlag === true &&
                            result.purchasedFlag === false
                        ) &&
                        fnGetParameter("tab") === "note"
                    ) {
                        setIsOpenNote(true);
                    }
                } else {
                    setIsLoadingFullvideo(false);
                }
            })
            .catch((error) => {
                console.error(error);
                // フル動画プレイヤーローディングを解除（エラーメッセージを出すため）
                setIsLoadingFullvideo(false);
            });
    }, []);

    /**
     * メインコンテンツ初期表示タブの確定
     */
    const refContentsMenu = useRef(null);
    useEffect(() => {
        if (props.isEditPage === true) {
            // 執刀医権限、管理者権限ページの場合
            setSelectedContentsData("preoperation");
        } else if (hasPointcontents === true && window.dataUser.isLogin === true) {
            // ポイントコンテンツがある場合
            setSelectedContentsData("pointcontents");
            refContentsMenu.current.scrollLeft = window.innerWidth;
        } else if (
            // 術前情報があり、fromとtabのパラメーターがない場合
            hasPreoperation === true &&
            !fnGetParameter("from") &&
            !fnGetParameter("tab")
        ) {
            setSelectedContentsData("preoperation");
            //setSelectedContentsData("fullvideo");
        } else {
            // 上記以外
            setSelectedContentsData("fullvideo");
        }
    }, [hasPreoperation, hasPointcontents]);

    /**
     * ユーザー情報取得
     */
    const [isFavorite, setIsFavorite] = useState(false); // お気に入り状態
    const [isRate, setIsRate] = useState(false); // 評価状態
    const [userPlayLogId, setUserPlayLogId] = useState(null); // ユーザープレイログID
    useEffect(() => {
        const api = "/api/videos/user/" + operationId;
        fetch(api)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    // お気に入り状態をセット
                    setIsFavorite(result.favoriteFlg);
                    // 評価状態をセット
                    setIsRate(result.ratingFlg);
                    // ユーザープレイログIDをセット
                    setUserPlayLogId(result.userPlayLogId);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    /**
     * フル動画プレイヤー関連
     */
    let isPlayForTransaction = false; // トランザクション、視聴履歴用の動画再生状態管理
    let preCurrentTime = 0; // 前回の動画再生位置
    const playRangeThreshold = 4; // 再生時間として加算する閾値（秒）
    const transactionThreshold = 60; // トランザクション更新閾値（秒）
    const videoPlayEventType = {
        Play: 1,
        pause: 2,
        Seeked: 3,
        PlayforInterval: 4,
    }; // 操作種別定義
    const videoPlayRatioType = {
        Ratio25: 25,
        Ratio50: 50,
        Ratio75: 75,
        Ratio100: 100,
    }; // 再生割合定義
    const videoPlayEventLog = {
        Play: 0,
        pause: 0,
        PreSeeking: 0,
        Seeking: 0,
        Seeked: 0,
        PlayRange: 0,
    }; // 操作ログ
    const [transactionId, setTransactionId] = useState(0); // トランザクションID
    const [playEvent, setPlayEvent] = useState(0); // 操作種別
    const [mediaPlayerFullvideo, setMediaPlayerFullvideo] = useState(null); // フル動画のMedia Player
    const [isCompletionMediaPlayerFullvideo, setIsCompletionMediaPlayerFullvideo] = useState(false); // フル動画のMedia Player設定状態
    const [isPlay, setIsPlay] = useState(false); // フル動画プレイヤー再生状
    const [timeProgressSecond, setTimeProgressSecond] = useState(0); // フル動画プレイヤー再生時間の秒換算
    const refFullvideoPlayerJumpButton = useRef(null); // ジャンプボタン
    const refFullvideoPlayerButtonlistSetting = useRef(null); // 設定ボタンリスト
    const refFullvideoPlayerIconlist = useRef(null); // アイコンリスト
    // DesktopPCアクセス時のMedia Player設定
    const fnSetRMP = () => {
        const timer = setInterval(() => {
            const rmpExtensions = document.querySelector(
                "#full .rmp-extensions"
            );
            const rmpProgress = document.querySelector(
                "#full .rmp-extensions .rmp-ext-progress"
            );
            const rmpSettings = document.querySelector(
                "#full .rmp-extensions .rmp-ext-settings"
            );
            if (
                rmpExtensions !== null &&
                rmpProgress !== null &&
                rmpSettings !== null
            ) {
                clearInterval(timer);
                // ジャンプボタン
                rmpExtensions.insertBefore(
                    refFullvideoPlayerJumpButton.current,
                    rmpProgress
                );
                // アイコンリスト（シークバーに重ねて表示）
                rmpExtensions.append(refFullvideoPlayerIconlist.current);
                // ボタンリスト（字幕、アイコン表示）
                rmpExtensions.insertBefore(
                    refFullvideoPlayerButtonlistSetting.current,
                    rmpSettings
                );
            }
        }, 500);
    };
    // ジャンプボタンのダブルクリックをプレイヤーに伝播させない
    useEffect(() => {
        const jumpButtonEl = refFullvideoPlayerJumpButton.current;
        const handleDblclick = (event) => {
            event.stopPropagation();
        };
        if (jumpButtonEl) {
            jumpButtonEl.addEventListener("dblclick", handleDblclick);
        }
        // クリーンアップ関数でイベントリスナーを削除
        return () => {
            if (jumpButtonEl) {
                jumpButtonEl.removeEventListener("dblclick", handleDblclick);
            }
        };
    }, [refFullvideoPlayerJumpButton.current]);

    // イベント追加
    useEffect(() => {
        if (mediaPlayerFullvideo !== null) {
            // playerの生成が間に合わない場合があるので遅延させる
            setTimeout(() => {
                // Android、iPad、iPhone以外の端末でプレイヤーにUI要素を追加
                if (
                    fnIsAndroid() === false &&
                    fnIsIpad() === false &&
                    fnIsIphone() === false
                ) {
                    fnSetRMP();
                }

                // ミュート設定
                if (!hasAudio) mediaPlayerFullvideo.muted = true;

                // 再生開始時間をセット
                const second = fnConvertTimeStringToSecond(timeProgress);
                mediaPlayerFullvideo.currentTime = second;

                // フル動画プレイヤーローディングを解除
                setIsLoadingFullvideo(false);

                // 再生状態切り替え処理（一時停止）
                mediaPlayerFullvideo.addEventListener("pause", () => {
                    setIsPlay(false);
                    isPlayForTransaction = false;
                    fnSetPlayerLog(videoPlayEventType.pause);
                });

                // 再生状態切り替え処理（再生）
                mediaPlayerFullvideo.addEventListener("play", () => {
                    setIsPlay(true);
                    isPlayForTransaction = true;
                    fnSetPlayerLog(videoPlayEventType.Play);
                });

                // 再生時間更新処理
                mediaPlayerFullvideo.addEventListener("timeupdate", () => {
                    const timeSecond = Math.round(
                        mediaPlayerFullvideo.currentTime
                    );
                    setTimeProgressSecond(timeSecond);
                    const timeString = fnConvertSecondToTimeString(timeSecond);
                    setTimeProgress(timeString);

                    // 前回から進んだ分を視聴時間として加算する
                    // （閾値を超える値またはマイナス値の場合はJump操作による値として無視する）
                    if (
                        0 < timeSecond - preCurrentTime &&
                        timeSecond - preCurrentTime < playRangeThreshold
                    ) {
                        // 視聴時間を加算する
                        videoPlayEventLog.PlayRange +=
                            timeSecond - preCurrentTime;
                    }
                    // 動画位置および記録時刻を保持
                    preCurrentTime = timeSecond;
                    // beforeunloadが使用できない環境もあるため、ディフォルトで周期的に視聴時間とトランザクション更新する
                    if (
                        isPlayForTransaction === true &&
                        videoPlayEventLog.PlayRange > transactionThreshold - 1
                    ) {
                        fnSetPlayerLog(videoPlayEventType.pause);
                        fnSetPlayerLog(videoPlayEventType.PlayforInterval);
                        //todo:暫定対処 setTimeout(() => {
                        //    fnSetPlayerLog(videoPlayEventType.Play);
                        //}, 500);
                        //todo:暫定対処 fnUpdateVideoAccessTransactionEndAt(transactionId);
                    }
                });

                // Google Tag Manager カスタムイベントトリガー
                //// フル動画が最後まで再生された
                mediaPlayerFullvideo.addEventListener(
                    "ended",
                    () => {
                        fnPushDataLayerGtm({
                            event: "CompleteFull",
                            operationId: window.atob(operationId),
                            userId: window.dataUser.userId,
                        });

                        setPlayEvent(videoPlayRatioType.Ratio100);
                    },
                    { once: true }
                );

                // SEEK開始
                mediaPlayerFullvideo.addEventListener("seeking", () => {
                    videoPlayEventLog.Seeking = Math.round(
                        mediaPlayerFullvideo.currentTime
                    );
                });

                // SEEK完了
                mediaPlayerFullvideo.addEventListener("seeked", () => {
                    // IOSの場合は2通りの再生方式あり
                    // ①ブラウザ上のコントローラ操作で、PCなど同様に再生状態ジャンプではpause→seeking→seeked→play
                    // ②専用コントローラ操作で、再生状態ジャンプであってもseeking→seekedのみ
                    // かつジャンプ中には動画の停止再生を受け付けない状態のためログ操作のみ実施
                    // （追記）上記は、azure media playerでのお話。ruvner playerでは全環境 seeking→seekedのイベント発行になった
                    if (
                        isPlayForTransaction === true
                    ) {
                        fnSetPlayerLog(videoPlayEventType.pause);
                        setTimeout(() => {
                            fnSetPlayerLog(videoPlayEventType.Seeked);
                        }, 500);
                        setTimeout(() => {
                            fnSetPlayerLog(videoPlayEventType.Play);
                        }, 1000);
                    } else {
                        fnSetPlayerLog(videoPlayEventType.Seeked);
                    }
                });
            }, 500);
            setTimeout(() => {
                setIsCompletionMediaPlayerFullvideo(true);
            }, estimateWaitTime());
        }
    }, [mediaPlayerFullvideo]);

    function estimateWaitTime() {
        const cores = navigator.hardwareConcurrency || 4; // CPU コア数
        if (!cores) return 1000;
        console.log(`cores: ${cores}`);
        if (cores >= 8) return 200;   // ハイエンド
        if (cores >= 4) return 1000;  // ミドル
        return 2000;                  // ローエンド
    }

    // フル動画プレイヤー再生状態管理変数とフル動画プレイヤーの実際の再生状態を合わせる
    useEffect(() => {
        if (mediaPlayerFullvideo !== null) {
            if (isPlay === true) {
                mediaPlayerFullvideo.play();
            } else {
                mediaPlayerFullvideo.pause();
            }
        }
    }, [mediaPlayerFullvideo, isPlay]);
    // タブ切り替え時、フル動画タブ以外ならフル動画プレイヤーを一時停止する
    useEffect(() => {
        if (isPlay === true && selectedContentsData !== "fullvideo") {
            setIsPlay(false);
        }
    }, [isPlay, selectedContentsData]);
    // 視聴履歴の登録
    useEffect(() => {
        if (playEvent === 0) return;
        fnAddVideoPlayerLog(
            playEvent,
            Math.round(mediaPlayerFullvideo.currentTime),
            0,
            transactionId
        );
    }, [playEvent]);
    // 00、30秒時にユーザープレイログ更新
    useEffect(() => {
        if (
            deviceLayout.length > 0 &&
            timeProgressSecond % 30 === 0 &&
            userPlayLogId !== null
        ) {
            fnUpdateUserplaylog(timeProgressSecond, userPlayLogId);
        }
    }, [deviceLayout, timeProgressSecond, userPlayLogId]);
    // アンロード時にユーザープレイログ更新
    const [isBeforeunload, setIsBeforeunload] = useState(false);
    useEffect(() => {
        window.addEventListener("beforeunload", () => {
            setIsBeforeunload(true);
        });
    }, []);
    useEffect(() => {
        if (isBeforeunload === true) {
            setIsPlay(false);
            fnUpdateUserplaylog(timeProgressSecond, userPlayLogId, false);
            //todo:暫定対処 setTimeout(() => {
            //    fnUpdateVideoAccessTransactionEndAt(transactionId);
            //}, 1000);
        }
    }, [isBeforeunload]);
    /**
     * タイムライン関連
     */
    // コメントリスト生成
    const [isResetListComment, setIsResetListComment] = useState(true); // コメントリストリセットフラグ
    const [listComment, setListComment] = useState(null); // コメントリスト
    useEffect(() => {
        if (isCompletionMediaPlayerFullvideo === true && isResetListComment === true && timeTotalSecond !== 0) {
            const api =
                "/api/comments/" +
                operationId +
                "?language=" +
                languageCodeOperation;
            fetch(api)
                .then(fnHandleApiErrors)
                .then(fnHandleApiSuccess)
                .then((result) => {
                    if (result !== null) {
                        const tempListComment = result
                            .filter((comment) => {
                                if (
                                    comment.commmentUserType === 1 && // 執刀医ユーザーのコメント
                                    comment.parentCommentId === null && // 親コメントのみ
                                    comment.isDeleted === false // 削除フラグがfalse
                                ) {
                                    return true;
                                }
                                return false;
                            })
                            .map((comment) => {
                                const position =
                                    (fnConvertTimeStringToSecond(
                                        comment.commentTime
                                    ) *
                                        100) /
                                    timeTotalSecond;
                                return {
                                    id: comment.commentId,
                                    position,
                                    text: comment.commentContext,
                                    time: comment.commentTime,
                                };
                            });
                        setListComment(tempListComment);
                    } else {
                        setListComment([]);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    setListComment([]);
                });
            setIsResetListComment(false);
        }
    }, [isResetListComment, timeTotalSecond, isCompletionMediaPlayerFullvideo]);
    // コメントに紐づく機器リスト生成
    const [listDevice, setListDevice] = useState(null); // コメントに紐づく機器リスト
    useEffect(() => {
        if (listComment !== null) {
            const api =
                "/api/SurgicalEquipment/comment/" +
                operationId +
                "?language=" +
                languageCodeOperation;
            fetch(api)
                .then(fnHandleApiErrors)
                .then(fnHandleApiSuccess)
                .then((result) => {
                    if (result !== null) {
                        setListDevice(result);
                    } else {
                        setListDevice([]);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    setListDevice([]);
                });
        }
    }, [listComment]);
    // コメントに紐づくシェーマリスト生成
    const [listSchema, setListSchema] = useState(null); // コメントに紐づくシェーマリスト
    useEffect(() => {
        if (listComment !== null) {
            const api =
                "/api/Shemas/" +
                operationId +
                "?language=" +
                languageCodeOperation;
            fetch(api)
                .then(fnHandleApiErrors)
                .then(fnHandleApiSuccess)
                .then((result) => {
                    if (result !== null) {
                        setListSchema(result);
                    } else {
                        setListSchema([]);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    setListSchema([]);
                });
        }
    }, [listComment]);
    // トピックリスト生成
    const [listTopic, setListTopic] = useState(null); // トピックリスト
    useEffect(() => {
        const apiEvents =
            "/api/topics/" + operationId + "?language=" + languageCodeOperation;
        fetch(apiEvents)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    const tempListTopic = result.topics.map((topic) => {
                        const tempListTopic = topic.comments.map((comment) => {
                            let userId, userInitial, userName;
                            if (comment.surgeon !== null) {
                                // コメントユーザーが医師の場合
                                userId = comment.surgeon.surgeonId;
                                userInitial =
                                    comment.surgeon.surgeonLastName.slice(0, 1);
                                userName = fnGetSurgeonFullName(
                                    {
                                        firstName:
                                            comment.surgeon.surgeonFirstName,
                                        lastName:
                                            comment.surgeon.surgeonLastName,
                                        middleName:
                                            comment.surgeon.surgeonMiddleName,
                                    },
                                    languageCodeOperation
                                );
                            } else if (comment.role !== null) {
                                // コメントユーザーが医師以外の場合
                                userId = null;
                                userInitial = comment.role.roleName.slice(0, 1);
                                userName =
                                    comment.role.roleName +
                                    " " +
                                    comment.role.roleNumber;
                            }
                            return {
                                text: comment.context,
                                time: comment.time,
                                userId,
                                userInitial,
                                userName,
                            };
                        });
                        const position =
                            (fnConvertTimeStringToSecond(topic.time) * 100) /
                            timeTotalSecond;
                        return {
                            id: topic.topicId,
                            listTopic: tempListTopic,
                            position,
                            text: topic.context,
                            time: topic.time,
                        };
                    });
                    setListTopic(tempListTopic);
                }
            })
            .catch((error) => {
                console.error(error);
                setListTopic([]);
            });
    }, [timeTotalSecond]);
    // タイムラインリスト生成
    const [isLoadingTimeline, setIsLoadingTimeline] = useState(true); // タイムラインローディング状態
    const [listTimeline, setListTimeline] = useState([]); // タイムラインリスト
    useEffect(() => {
        if (
            isCompletionMediaPlayerFullvideo === true &&
            listComment !== null &&
            listDevice !== null &&
            listSchema !== null &&
            listTopic !== null
        ) {
            const tempListComment = listComment.map((comment) => {
                // シェーマリストをコメントリストと結合
                const tempListSchema = listSchema
                    .filter((schema) => {
                        if (comment.id !== schema.commentId) {
                            return false;
                        }
                        return true;
                    })
                    .map((schema) => {
                        return {
                            failName: schema.failName,
                            fileUrl: schema.fileUrl,
                            id: schema.commentId,
                            time: schema.operationTime,
                        };
                    });
                comment.listSchema = tempListSchema;
                // 機器リストをコメントリストと結合
                const tempListDevice = listDevice
                    .filter((device) => {
                        if (comment.id !== device.commentId) {
                            return false;
                        }
                        return true;
                    })
                    .map((device) => {
                        const deviceName =
                            device.eqptName +
                            " " +
                            device.manufacturer +
                            " " +
                            device.standardSize;
                        return {
                            deviceName,
                            id: device.commentId,
                            linkUrl: device.linkUrl,
                            time: device.operationTime,
                        };
                    });
                comment.listDevice = tempListDevice;
                return comment;
            });
            // コメントリストとトピックリストを結合し、時間順にソート
            let tempListTimeline;
            if (
                // 執刀医権限、管理者権限でない有料コンテンツで未購入の場合、トピックリストは結合しない
                props.isEditPage !== true &&
                isPaid === true &&
                isPurchased === false
            ) {
                tempListTimeline = tempListComment;
            } else {
                tempListTimeline = tempListComment.concat(listTopic);
            }
            tempListTimeline.sort((a, b) => {
                if (a.time > b.time) {
                    return 1;
                }
                if (b.time > a.time) {
                    return -1;
                }
                return 0;
            });
            if (
                props.isEditPage !== true &&
                isPaid === true &&
                isPurchased === false
            ) {
                // 執刀医権限、管理者権限でない有料コンテンツで未購入の場合、最初の5件のみ出力させる
                setListTimeline(tempListTimeline.slice(0, 5));
            } else if (
                !window.dataUser.isLogin
            ) {
                // SEO対応：未ログインの場合、最初の5件のみ出力させる
                setListTimeline(tempListTimeline.slice(0, 5));
            } else {
                // 上記以外の場合、全て出力させる
                setListTimeline(tempListTimeline);
            }
            setIsLoadingTimeline(false);
        }
    }, [isPaid, isPurchased, listComment, listDevice, listSchema, listTopic, isCompletionMediaPlayerFullvideo]);
    // イベントリスト生成
    const [isResetListEvent, setIsResetListEvent] = useState(true); // イベントリストリセットフラグ
    const [listEvent, setListEvent] = useState([]); // イベントリスト
    useEffect(() => {
        if (isResetListEvent === true && timeTotalSecond > 0) {
            const apiEvents =
                "/api/events/get/" +
                operationId +
                "?language=" +
                languageCodeOperation;
            fetch(apiEvents)
                .then(fnHandleApiErrors)
                .then(fnHandleApiSuccess)
                .then((result) => {
                    if (result !== null) {
                        const tempListEvent = result.map((event) => {
                            const positionIcon =
                                (fnConvertTimeStringToSecond(event.eventTime) *
                                    100) /
                                timeTotalSecond;
                            return {
                                id: event.eventId,
                                position: positionIcon,
                                text: event.eventContext,
                                time: event.eventTime,
                            };
                        });
                        setListEvent(tempListEvent);
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
            setIsResetListEvent(false);
        }
    }, [isResetListEvent, timeTotalSecond]);

    /**
     * 機器広告取得
     */
    const [adOperationButtonText, setAdOperationButtonText] = useState(null); // ボタン表示テキスト
    const [adOperationDeviceName, setAdOperationDeviceName] = useState(null); // 機器広告名
    const [adOperationId, setAdOperationId] = useState(null); // 機器広告ID
    const [adOperationLink, setAdOperationLink] = useState(null); // 機器広告リンク
    const [adOperationSummary, setAdOperationSummary] = useState(null); // 機器広告概要
    const [isOpenModalAdConfirm, setIsOpenModalAdConfirm] = useState(false); // 機器広告問い合わせ確認モーダル開閉状態
    const [isOpenModalAdFailure, setIsOpenModalAdFailure] = useState(false); // 機器広告問い合わせ失敗モーダル開閉状態
    const [isOpenModalAdSuccess, setIsOpenModalAdSuccess] = useState(false); // 機器広告問い合わせ成功モーダル開閉状態
    const [isOpenPopupAd, setIsOpenPopupAd] = useState(false); // 機器広告ポップアップ開閉状態
    const [isSponsorContent, setIsSponsorContent] = useState(false); // スポンサーコンテンツかどうか
    useEffect(() => {
        const api =
            "/api/adOperations/" +
            operationId +
            "?language=" +
            languageCodeOperation;
        fetch(api)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    setAdOperationButtonText(result.buttonText);
                    setAdOperationDeviceName(result.deviceName);
                    setAdOperationId(result.adOperationID);
                    setAdOperationLink(result.materialUrl);
                    setAdOperationSummary(result.summary);
                    setIsSponsorContent(true);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    /**
     * 症例ページのリンクボタンの取得
     */
    const [contentPageLinkButtonText, setContentPageLinkButtonText] = useState(null);
    const [contentPageLink, setContentPageLink] = useState(null);
    const [contentPageLinkAnotherTabFlag, setContentPageLinkAnotherTabFlag] = useState(null);
    useEffect(() => {
        const api =
            "/api/contentPageLinks/" +
            operationId +
            "?language=" +
            languageCodeOperation;
        fetch(api)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    // 内部リンク先が絶対パスになっている場合、相対パスに変更
                    const targetLink = result.linkUrl;
                    const urlOrigin = location.origin;
                    const relativeLink =
                        targetLink.indexOf(urlOrigin) !== -1 &&
                            !result.anotherTabFlag
                            ? targetLink.replace(urlOrigin, "")
                            : targetLink;
                    setContentPageLink(relativeLink);
                    setContentPageLinkButtonText(result.buttonText);
                    setContentPageLinkAnotherTabFlag(result.anotherTabFlag);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    /**
     * その他有料コンテンツの取得
     */
    const [operationlistPaid, setOperationlistPaid] = useState([]); // その他有料コンテンツの症例リスト
    useEffect(() => {
        if (isPaid === true) {
            const api =
                "/api/operations/productList/" +
                operationId +
                "?language=" +
                languageCodeUI +
                "&limit=5&paidContentOnly=true";
            fetch(api)
                .then(fnHandleApiErrors)
                .then(fnHandleApiSuccess)
                .then((result) => {
                    if (result !== null) {
                        // 症例リストを設定
                        const tempOperationlist = result.operationCards.map(
                            (operation) => {
                                // 執刀医リスト生成
                                const surgeonlist = operation.surgeons.map(
                                    (surgeon) => {
                                        return {
                                            id: surgeon.surgeonId,
                                            name: fnGetSurgeonFullName(
                                                surgeon,
                                                languageCodeUI
                                            ),
                                        };
                                    }
                                );
                                // 症例データ生成
                                return {
                                    canEdit: false,
                                    date: operation.operationUploadDate
                                        .slice(0, 10)
                                        .split("-")
                                        .join("/"),
                                    isOperation: true,
                                    isPurchased: operation.purchasedFlag,
                                    link: operation.videoUrl,
                                    nolinkSurgeonlist: true,
                                    price: operation.unitAmount,
                                    rate: operation.ratingCount,
                                    surgeonlist,
                                    thumbnail: operation.shortMovieUrl,
                                    thumbnailAnimation: operation.shortMovieUrl,
                                    title: operation.operationTitle,
                                    view: operation.viewCount,
                                    id: operation.operationId,
                                };
                            }
                        );
                        setOperationlistPaid(tempOperationlist);
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }, [isPaid]);

    /**
     * 同じカテゴリー（術式分類）の症例取得
     */
    const [operationlistCategory, setOperationlistCategory] = useState([]); // 同じカテゴリーの症例リスト
    useEffect(() => {
        if (hasPointcontents === false) {
            const api =
                "/api/Operations/surgical/" +
                operationId +
                "?language=" +
                languageCodeUI;
            fetch(api)
                .then(fnHandleApiErrors)
                .then(fnHandleApiSuccess)
                .then((result) => {
                    if (result !== null) {
                        // 症例リストを設定
                        const tempOperationlist = result.operationCards.map(
                            (operation) => {
                                // 執刀医リスト生成
                                const surgeonlist = operation.surgeons.map(
                                    (surgeon) => {
                                        return {
                                            id: surgeon.surgeonId,
                                            name: fnGetSurgeonFullName(
                                                surgeon,
                                                languageCodeUI
                                            ),
                                        };
                                    }
                                );
                                // 症例データ生成
                                return {
                                    canEdit: false,
                                    date: operation.operationUploadDate
                                        .slice(0, 10)
                                        .split("-")
                                        .join("/"),
                                    isOperation: true,
                                    link: operation.videoUrl,
                                    nolinkSurgeonlist: true,
                                    rate: operation.ratingCount,
                                    surgeonlist,
                                    thumbnail: operation.shortMovieUrl,
                                    thumbnailAnimation: operation.shortMovieUrl,
                                    title: operation.operationTitle,
                                    view: operation.viewCount,
                                    id: operation.operationId,
                                };
                            }
                        );
                        setOperationlistCategory(tempOperationlist);
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }, [hasPointcontents]);

    /**
     * 同じ執刀医の症例取得
     */
    const [operationlistSurgeon, setOperationlistSurgeon] = useState([]); // 同じ執刀医の症例リスト
    useEffect(() => {
        if (hasPointcontents === false) {
            const api =
                "/api/Operations/surgeon/" +
                operationId +
                "?language=" +
                languageCodeUI;
            fetch(api)
                .then(fnHandleApiErrors)
                .then(fnHandleApiSuccess)
                .then((result) => {
                    if (result !== null) {
                        // 症例リストを設定
                        const tempOperationlist = result.operationCards.map(
                            (operation) => {
                                // 執刀医リスト生成
                                const surgeonlist = operation.surgeons.map(
                                    (surgeon) => {
                                        return {
                                            id: surgeon.surgeonId,
                                            name: fnGetSurgeonFullName(
                                                surgeon,
                                                languageCodeUI
                                            ),
                                        };
                                    }
                                );
                                // 症例データ生成
                                return {
                                    canEdit: false,
                                    date: operation.operationUploadDate
                                        .slice(0, 10)
                                        .split("-")
                                        .join("/"),
                                    isOperation: true,
                                    link: operation.videoUrl,
                                    nolinkSurgeonlist: true,
                                    rate: operation.ratingCount,
                                    surgeonlist,
                                    thumbnail: operation.shortMovieUrl,
                                    thumbnailAnimation: operation.shortMovieUrl,
                                    title: operation.operationTitle,
                                    view: operation.viewCount,
                                    id: operation.operationId,
                                };
                            }
                        );
                        setOperationlistSurgeon(tempOperationlist);
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }, [hasPointcontents]);

    /**
     * 同じカテゴリー（ポイントコンテンツ）の症例取得
     */
    // 同じカテゴリー（ポイントコンテンツ）の症例リスト
    const [operationlistPointcontents, setOperationlistPointcontents] =
        useState([]);
    useEffect(() => {
        if (hasPointcontents === true) {
            const api =
                "/api/operations/point/" +
                operationId +
                "?language=" +
                languageCodeUI;
            fetch(api)
                .then(fnHandleApiErrors)
                .then(fnHandleApiSuccess)
                .then((result) => {
                    if (result !== null) {
                        // 症例リストを設定
                        const tempOperationlist = result.operationCards.map(
                            (operation) => {
                                // 執刀医リスト生成
                                const surgeonlist = operation.surgeons.map(
                                    (surgeon) => {
                                        return {
                                            id: surgeon.surgeonId,
                                            name: fnGetSurgeonFullName(
                                                surgeon,
                                                languageCodeUI
                                            ),
                                        };
                                    }
                                );
                                // 症例データ生成
                                return {
                                    canEdit: false,
                                    date: operation.operationUploadDate
                                        .slice(0, 10)
                                        .split("-")
                                        .join("/"),
                                    isOperation: true,
                                    link: operation.videoUrl,
                                    nolinkSurgeonlist: true,
                                    rate: operation.ratingCount,
                                    surgeonlist,
                                    thumbnail: operation.shortMovieUrl,
                                    thumbnailAnimation: operation.shortMovieUrl,
                                    title: operation.operationTitle,
                                    view: operation.viewCount,
                                    id: operation.operationId,
                                };
                            }
                        );
                        setOperationlistPointcontents(tempOperationlist);
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }, [hasPointcontents]);

    /**
     * 関連コンテンツの症例取得
     */
    // 関連コンテンツの症例リスト
    const [operationlistRelatedContent, setOperationlistRelatedContent] =
        useState([]);
    useEffect(() => {
        if (hasPointcontents === false) {
            const api =
                "/api/operations/relatedContent/" +
                operationId +
                "?language=" +
                languageCodeUI;
            fetch(api)
                .then(fnHandleApiErrors)
                .then(fnHandleApiSuccess)
                .then((result) => {
                    if (result !== null) {
                        // 症例リストを設定
                        const tempOperationlist = result.operationCards.map(
                            (operation) => {
                                // 執刀医リスト生成
                                const surgeonlist = operation.surgeons.map(
                                    (surgeon) => {
                                        return {
                                            id: surgeon.surgeonId,
                                            name: fnGetSurgeonFullName(
                                                surgeon,
                                                languageCodeUI
                                            ),
                                        };
                                    }
                                );
                                // 症例データ生成
                                return {
                                    canEdit: false,
                                    date: operation.operationUploadDate
                                        .slice(0, 10)
                                        .split("-")
                                        .join("/"),
                                    isOperation: true,
                                    link: operation.videoUrl,
                                    nolinkSurgeonlist: true,
                                    rate: operation.ratingCount,
                                    surgeonlist,
                                    thumbnail: operation.shortMovieUrl,
                                    thumbnailAnimation: operation.shortMovieUrl,
                                    title: operation.operationTitle,
                                    view: operation.viewCount,
                                    id: operation.operationId,
                                };
                            }
                        );
                        setOperationlistRelatedContent(tempOperationlist);
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }, [hasPointcontents]);

    /**
     * 閲覧履歴取得
     */
    const [operationlistBrowsingHistory, setOperationlistBrowsingHistory] =
        useState([]);
    useEffect(() => {
        const api =
            "/mypage/PagingPlayLogCases?operationId=" +
            operationId +
            "&language=" +
            languageCodeUI;
        fetch(api)
            .then(fnHandleApiErrors)
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    // 症例リストを設定
                    const tempOperationlist = result.map((operation) => {
                        // 執刀医リスト生成
                        const surgeonlist = operation.surgeons.map(
                            (surgeon) => {
                                return {
                                    id: surgeon.surgeonId,
                                    name: fnGetSurgeonFullName(
                                        surgeon,
                                        languageCodeUI
                                    ),
                                };
                            }
                        );
                        // 症例データ生成
                        return {
                            canEdit: false,
                            date: operation.operationUploadDate
                                .slice(0, 10)
                                .split("-")
                                .join("/"),
                            isOperation: true,
                            link: operation.videoUrl,
                            nolinkSurgeonlist: true,
                            rate: operation.ratingCount,
                            surgeonlist,
                            thumbnail: operation.shortMovieUrl,
                            thumbnailAnimation: operation.shortMovieUrl,
                            title: operation.operationTitle,
                            view: operation.viewCount,
                            id: operation.operationId,
                        };
                    });
                    setOperationlistBrowsingHistory(tempOperationlist);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    /**
     * 編集モーダルに渡す処理対象DOMを指定
     */

    /**
     * シェーマポップアップ状態管理
     */
    const [clickedSlideIndex, setClickedSlideIndex] = useState(null); // クリックされたシェーマスライド位置
    const [isOpenPopupSchema, setIsOpenPopupSchema] = useState(false); // シェーマポップ開閉状態
    const [popupSchemaSlider, setPopupSchemaSlider] = useState(null); // ポップアップに表示するシェーマリスト

    /**
     * ノート状態管理
     */
    const [isOpenNote, setIsOpenNote] = useState(false); // ノートポップアップ開閉状態

    /**
     * スレッドポップアップ開閉状態管理
     */
    const [isOpenPopupThread, setIsOpenPopupThread] = useState(false); // スレッドポップアップ開閉状態

    /**
     * 決済情報ポップアップ開閉状態管理
     */
    const [isHighlightPopupPayment, setIsHighlightPopupPayment] = // 決済情報ポップアップ強調表示状態
        useState(false);
    const [isOpenPopupPayment, setIsOpenPopupPayment] = useState(false); // 決済情報ポップアップ開閉状態
    // 強調表示がtrueになったら決済情報ポップアップを開き強調表示を開始、1秒後に強調表示を消す
    useEffect(() => {
        if (isHighlightPopupPayment === true) {
            setIsOpenPopupPayment(true);
            setTimeout(() => {
                setIsHighlightPopupPayment(false);
            }, 1000);
        }
    }, [isHighlightPopupPayment]);

    /**
     * 編集モーダル状態管理
     */
    const [isOpenModalEdit, setIsOpenModalEdit] = useState(false); // 編集モーダル開閉状態
    const [modalEditContent, setModalEditContent] = useState(null); // 編集モーダルのコンテンツ
    const [modalEditMode, setModalEditMode] = useState(null); // 編集モーダルのモード
    const [modalEditType, setModalEditType] = useState(null); // 編集モーダルのタイプ
    const [surgeonUserList, setSurgeonUserList] = useState([]); // 執刀医権限ユーザーリスト
    const refSummary = useRef(null);
    const refTitle = useRef(null);
    useEffect(() => {
        if (window.dataSurgeonUserList !== undefined) {
            setSurgeonUserList(window.dataSurgeonUserList);
        }
    }, []);

    /**
     * セッションエラーモーダル状態管理
     */
    const [isOpenModalErrorSession, setIsOpenModalErrorSession] = // セッションエラーモーダル開閉状態
        useState(false);

    /**
     * 利用規約モーダル状態管理
     */
    const [isOpenModalTerms, setIsOpenModalTerms] = useState(false); // 利用規約モーダル開閉状態

    /**
     * Google Tag Manager カスタムイベントトリガー
     */
    useEffect(() => {
        // スポンサーコンテンツのフル動画ページに遷移した
        if (isSponsorContent) {
            fnPushDataLayerGtm({
                event: "ContentViewCount",
                operationId: window.atob(operationId),
                userId: window.dataUser.userId,
            });
        }
    }, [isSponsorContent]);

    /**
     * 関数
     * ------------------------------------------------------------
     */

    /**
     * フル動画プレイヤー再生時間のジャンプ
     * @param time 時間（hh:mm:ss）
     */
    const fnJump = (time: string) => {
        // 処理前の再生状態の保持、および再生の一時停止
        const tempIsPlay = isPlay;
        if (tempIsPlay === true) {
            setIsPlay(false);
        }
        // 再生速度仮設定
        let tempPlaybackRate = 1;
        // フル動画プレイヤーを表示
        setSelectedContentsData("fullvideo");
        // 現在時間のリセット
        setTimeProgress(null);
        if (mediaPlayerFullvideo !== null) {
            // Media Player生成できている場合
            // 処理前の再生速度を取得
            tempPlaybackRate = mediaPlayerFullvideo.playbackRate;
            // 指定時間にジャンプ
            const second = fnConvertTimeStringToSecond(time);
            mediaPlayerFullvideo.currentTime = second;
            // ユーザープレイログ更新（定期更新タイミングと重複しないように00、30秒時は除外）
            if (second % 30 !== 0) {
                fnUpdateUserplaylog(second, userPlayLogId);
            }
        } else {
            // Media Player生成できていない場合、エラーメッセージ
            alert(localized["ErrorMessage.FailLoadVideo"]);
        }
        // 処理前の再生状態に応じて再生状態を変更
        setTimeout(() => {
            if (tempIsPlay === true) {
                setIsPlay(true);
            }
            // 処理前の再生速度を引き継ぐ
            mediaPlayerFullvideo.playbackRate = tempPlaybackRate;
        }, 500);
    };

    /**
     * 10分毎のセッション更新
     */
    const fnSeeionRefresh = () => {
        // SEO対応：ログイン前なら処理しない
        if (window.dataUser.isLogin === false) {
            return;
        }
        const token = document
            .getElementById("requestverificationtoken")
            .getAttribute("value");
        const fnSessionRefreshInterval = setInterval(() => {
            const api = "/api/Sessions/refresh";
            const options = {
                headers: {
                    "Content-Type": "application/json",
                    RequestVerificationToken: token,
                },
                method: "POST",
            };
            fetch(api, options)
                .then((res) => {
                    // セッション切れ時にセッションエラーモーダルを開く
                    if (res.status !== 200 || res.url.indexOf(api) === -1) {
                        clearInterval(fnSessionRefreshInterval);
                        setIsPlay(false);
                        setIsOpenModalErrorSession(true);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    // セッション切れ時にセッションエラーモーダルを開く
                    clearInterval(fnSessionRefreshInterval);
                    setIsPlay(false);
                    setIsOpenModalErrorSession(true);
                });
        }, 600000);
        setInterval(() => {
            const api = "/api/Connections/update";
            const options = {
                headers: {
                    "Content-Type": "application/json",
                    RequestVerificationToken: token,
                },
                method: "POST",
            };
            fetch(api, options).catch((error) => {
                console.error(error);
            });
        }, 600000);
    };
    useEffect(() => {
        //SEO対応：ログイン前でも症例画面を表示するためセッション確認不要
        //fnSeeionRefresh();
    }, []);

    /**
     * 編集モーダルのセッティング
     */
    const fnSetModalEdit = (e, type, mode, content) => {
        // クリックのデフォルトイベント無効化
        e.preventDefault();
        // 一時停止にする
        setIsPlay(false);
        // 各状態変数のセット
        setModalEditContent(content);
        setModalEditMode(mode);
        setModalEditType(type);
        setIsOpenModalEdit(true);
    };

    /**
     * ユーザープレイログの更新
     * @param valueProgressTime 再生時間（秒）
     * @param valueUserPlayLogId ユーザープレイログID
     * @param flagAsync asyncフラグ
     */
    const fnUpdateUserplaylog = (
        valueProgressTime = timeProgressSecond,
        valueUserPlayLogId = userPlayLogId,
        flagAsync = true
    ) => {
        // SEO対応：ログイン前なら処理しない
        if (window.dataUser.isLogin === false) {
            return;
        }
        const params = {
            deviceLayout,
            displayNo: 1,
            operationId: decodeURIComponent(operationId),
            progressTime: valueProgressTime,
            userPlayLogId: valueUserPlayLogId,
        };
        const api = "/api/UserPlayLogs/update";
        const options = {
            body: JSON.stringify(params),
            headers: {
                "Content-Type": "application/json",
                RequestVerificationToken: document
                    .getElementById("requestverificationtoken")
                    .getAttribute("value"),
            },
            method: "POST",
        };
        if (flagAsync === true) {
            fetch(api, options).then((response) => {
                // セッション切れ時にセッションエラーモーダルを開く
                if (response.url.indexOf(api) === -1) {
                    setIsPlay(false);
                    setIsOpenModalErrorSession(true);
                }
            });
        } else {
            fetch(api, options);
        }
    };

    /**
     * トランザクションの追加
     * @param valueOperationId 症例ID
     */
    useEffect((valueOperationId = operationId) => {
        // SEO対応：ログイン前なら処理しない
        if (window.dataUser.isLogin === false) {
            return;
        }
        const params = {
            operationId: valueOperationId,
        };
        const api = "/api/AccessTransactions/videoaccess";
        const options = {
            body: JSON.stringify(params),
            headers: {
                "Content-Type": "application/json",
                RequestVerificationToken: document
                    .getElementById("requestverificationtoken")
                    .getAttribute("value"),
            },
            method: "POST",
        };
        fetch(api, options)
            .then((response) => {
                // セッション切れ時にセッションエラーモーダルを開く
                if (response.url.indexOf(api) === -1) {
                    setIsPlay(false);
                    setIsOpenModalErrorSession(true);
                }
                // エラーではない場合
                return response;
            })
            .then(fnHandleApiSuccess)
            .then((result) => {
                if (result !== null) {
                    setTransactionId(result.TransactionId);
                    // 環境によりbeforeunloadが使用できないためディフォルト初期化
                    //todo:暫定対処 fnUpdateVideoAccessTransactionEndAt(result.TransactionId);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    /**
     * トランザクションの更新
     * @param valueTransactionId トランザクションID
     */
    const fnUpdateVideoAccessTransactionEndAt = (
        valueTransactionId = transactionId
    ) => {
        // SEO対応：ログイン前なら処理しない
        if (window.dataUser.isLogin === false) {
            return;
        }
        const params = {
            transactionId: valueTransactionId,
        };
        const api = "/api/AccessTransactions/videoaccess";
        const options = {
            body: JSON.stringify(params),
            headers: {
                "Content-Type": "application/json",
                RequestVerificationToken: document
                    .getElementById("requestverificationtoken")
                    .getAttribute("value"),
            },
            method: "PUT",
        };
        fetch(api, options)
            .then((response) => {
                // セッション切れ時にセッションエラーモーダルを開く
                if (response.url.indexOf(api) === -1) {
                    setIsPlay(false);
                    setIsOpenModalErrorSession(true);
                }
                // エラーではない場合
                return response;
            })
            .then(fnHandleApiSuccess)
            .catch((error) => {
                console.error(error);
            });
    };

    /**
     * 視聴履歴の追加
     * @param valueTransactionId トランザクションID
     * @param valuePlayEvent 操作種別
     * @param valueVideoProgressTime 動画時刻
     * @param valueVideoPlayRanges 動画の視聴時間
     */
    const fnAddVideoPlayerLog = (
        valuePlayEvent: number,
        valueVideoProgressTime: number,
        valueVideoPlayRanges: number,
        valueTransactionId = transactionId
    ) => {
        // SEO対応：ログイン前なら処理しない
        if (window.dataUser.isLogin === false) {
            return;
        }
        const params = {
            transactionId: valueTransactionId,
            playEvent: valuePlayEvent,
            videoProgressTime: valueVideoProgressTime,
            videoPlayRanges: valueVideoPlayRanges,
        };
        const api = "/api/AccessTransactions/playerLog";
        const options = {
            body: JSON.stringify(params),
            headers: {
                "Content-Type": "application/json",
                RequestVerificationToken: document
                    .getElementById("requestverificationtoken")
                    .getAttribute("value"),
            },
            method: "POST",
        };
        fetch(api, options)
            .then((response) => {
                // セッション切れ時にセッションエラーモーダルを開く
                if (response.url.indexOf(api) === -1) {
                    setIsPlay(false);
                    setIsOpenModalErrorSession(true);
                }
                // エラーではない場合
                return response;
            })
            .then(fnHandleApiSuccess)
            .catch((error) => {
                console.error(error);
            });
    };

    /**
     * 視聴履歴ログ
     * @param valuePlayEvent 操作種別
     */
    const fnSetPlayerLog = (valuePlayEvent: number) => {
        const timeSecond = Math.round(mediaPlayerFullvideo.currentTime);
        if (valuePlayEvent === videoPlayEventType.Play) {
            videoPlayEventLog.Play = timeSecond;
            fnAddVideoPlayerLog(
                videoPlayEventType.Play,
                videoPlayEventLog.Play,
                0,
                transactionId
            );
            // 前回の動画再生位置および視聴時間の記録初期化
            preCurrentTime = videoPlayEventLog.Play;
            videoPlayEventLog.PlayRange = 0;
        } else if (valuePlayEvent === videoPlayEventType.PlayforInterval) {
            videoPlayEventLog.Play = timeSecond;
            // 前回の動画再生位置および視聴時間の記録初期化
            preCurrentTime = videoPlayEventLog.Play;
            videoPlayEventLog.PlayRange = 0;
        } else if (valuePlayEvent === videoPlayEventType.pause) {
            videoPlayEventLog.pause = timeSecond;
            // ジャンプ前の時刻は再生開始位置に視聴時間を加える
            videoPlayEventLog.PreSeeking =
                videoPlayEventLog.Play + videoPlayEventLog.PlayRange;
            fnAddVideoPlayerLog(
                videoPlayEventType.pause,
                videoPlayEventLog.PreSeeking,
                videoPlayEventLog.PlayRange,
                transactionId
            );
            // 視聴時間の記録初期化
            videoPlayEventLog.PlayRange = 0;
        } else if (valuePlayEvent === videoPlayEventType.Seeked) {
            videoPlayEventLog.Seeked = timeSecond;
            fnAddVideoPlayerLog(
                videoPlayEventType.Seeked,
                videoPlayEventLog.Seeked,
                0,
                transactionId
            );
        }
    };

    // Presenterに渡す
    return (
        <Presenter
            {...props}
            // 全体で使用
            fnJump={fnJump}
            fnSetModalEdit={fnSetModalEdit}
            hasPostoperation={hasPostoperation}
            hasPreoperation={hasPreoperation}
            isIdentificationUser={isIdentificationUser}
            isSponsorContent={isSponsorContent}
            isSponsorUser={isSponsorUser}
            languageCodeOperation={languageCodeOperation}
            listSurgicalEquipment={listSurgicalEquipment}
            operationId={operationId}
            refContentsMenu={refContentsMenu}
            // 手術情報：Contents全体で使用
            isPaid={isPaid}
            isPurchased={isPurchased}
            mainSurgeonId={mainSurgeonId}
            timeProgress={timeProgress}
            productId={productId}
            // 手術情報：ContentsControllerで使用
            isLoadingContentsController={isLoadingContentsController}
            // 手術情報：ContentsDataで使用（FullvideoPlayer専用以外）
            date={date}
            hasDigest={hasDigest}
            hasPointcontents={hasPointcontents}
            isLoadingContentsData={isLoadingContentsData}
            linkRelatedCasesCategory={linkRelatedCasesCategory}
            linkRelatedCasesSurgeon={linkRelatedCasesSurgeon}
            listLanguage={listLanguage}
            operationlistBrowsingHistory={operationlistBrowsingHistory}
            operationlistCategory={operationlistCategory}
            operationlistPaid={operationlistPaid}
            operationlistPointcontents={operationlistPointcontents}
            operationlistRelatedContent={operationlistRelatedContent}
            operationlistSurgeon={operationlistSurgeon}
            rateCount={rateCount}
            refSummary={refSummary}
            refTitle={refTitle}
            selectedContentsData={selectedContentsData}
            setRateCount={setRateCount}
            setSelectedContentsData={setSelectedContentsData}
            setTaglist={setTaglist}
            slidersPostoperation={slidersPostoperation}
            slidersPreoperation={slidersPreoperation}
            summary={summary}
            surgeonlist={surgeonlist}
            surgeonUserList={surgeonUserList}
            taglist={taglist}
            textPostoperation={textPostoperation}
            textPreoperation={textPreoperation}
            title={title}
            view={view}
            // 手術情報：FullvideoPlayerで使用
            fairplayCer={fairplayCer}
            fairplayLaUrl={fairplayLaUrl}
            hasAudio={hasAudio}
            movieUrl={movieUrl}
            playreadyLaUrl={playreadyLaUrl}
            timeTotalSecond={timeTotalSecond}
            widevineLaUrl={widevineLaUrl}
            // ユーザー情報
            isFavorite={isFavorite}
            isRate={isRate}
            setIsFavorite={setIsFavorite}
            setIsRate={setIsRate}
            userPlayLogId={userPlayLogId}
            // フル動画プレイヤー関連
            isLoadingFullvideo={isLoadingFullvideo}
            isPlay={isPlay}
            refFullvideoPlayerButtonlistSetting={
                refFullvideoPlayerButtonlistSetting
            }
            refFullvideoPlayerIconlist={refFullvideoPlayerIconlist}
            refFullvideoPlayerJumpButton={refFullvideoPlayerJumpButton}
            setMediaPlayerFullvideo={setMediaPlayerFullvideo}
            setPlayEvent={setPlayEvent}
            timeProgressSecond={timeProgressSecond}
            videoPlayRatioType={videoPlayRatioType}
            // タイムライン関連
            isLoadingTimeline={isLoadingTimeline}
            listComment={listComment}
            listEvent={listEvent}
            listTopic={listTopic}
            listTimeline={listTimeline}
            setIsResetListComment={setIsResetListComment}
            setIsResetListEvent={setIsResetListEvent}
            // 機器広告
            adOperationButtonText={adOperationButtonText}
            adOperationDeviceName={adOperationDeviceName}
            adOperationId={adOperationId}
            adOperationLink={adOperationLink}
            adOperationSummary={adOperationSummary}
            isOpenModalAdConfirm={isOpenModalAdConfirm}
            isOpenModalAdFailure={isOpenModalAdFailure}
            isOpenModalAdSuccess={isOpenModalAdSuccess}
            isOpenPopupAd={isOpenPopupAd}
            setIsOpenModalAdConfirm={setIsOpenModalAdConfirm}
            setIsOpenModalAdFailure={setIsOpenModalAdFailure}
            setIsOpenModalAdSuccess={setIsOpenModalAdSuccess}
            setIsOpenPopupAd={setIsOpenPopupAd}
            // 症例ページのリンクボタン
            contentPageLink={contentPageLink}
            contentPageLinkAnotherTabFlag={contentPageLinkAnotherTabFlag}
            contentPageLinkButtonText={contentPageLinkButtonText}
            // シェーマポップアップ
            clickedSlideIndex={clickedSlideIndex}
            isOpenPopupSchema={isOpenPopupSchema}
            popupSchemaSlider={popupSchemaSlider}
            setClickedSlideIndex={setClickedSlideIndex}
            setIsOpenPopupSchema={setIsOpenPopupSchema}
            setPopupSchemaSlider={setPopupSchemaSlider}
            // ノート
            isOpenNote={isOpenNote}
            setIsOpenNote={setIsOpenNote}
            // スレッドポップアップ
            isOpenPopupThread={isOpenPopupThread}
            setIsOpenPopupThread={setIsOpenPopupThread}
            // 決済情報ポップアップ
            isHighlightPopupPayment={isHighlightPopupPayment}
            isOpenPopupPayment={isOpenPopupPayment}
            setIsHighlightPopupPayment={setIsHighlightPopupPayment}
            setIsOpenPopupPayment={setIsOpenPopupPayment}
            // 編集モーダル
            isOpenModalEdit={isOpenModalEdit}
            modalEditContent={modalEditContent}
            modalEditMode={modalEditMode}
            modalEditType={modalEditType}
            setIsOpenModalEdit={setIsOpenModalEdit}
            setModalEditContent={setModalEditContent}
            setModalEditMode={setModalEditMode}
            setModalEditType={setModalEditType}
            setSurgeonUserList={setSurgeonUserList}
            // セッションエラーモーダル
            isOpenModalErrorSession={isOpenModalErrorSession}
            // 利用規約モーダル
            isOpenModalTerms={isOpenModalTerms}
            setIsOpenModalTerms={setIsOpenModalTerms}
            // ローディングエラーモーダル
            isOpenModalErrorLoading={isOpenModalErrorLoading}
            setIsOpenModalErrorLoading={setIsOpenModalErrorLoading}
            // 性能改善
            isCompletionMediaPlayerFullvideo={isCompletionMediaPlayerFullvideo}
        />
    );
};
